import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';

interface PollOption {
  id: string;
  emoji: string;
  label?: string;
}

interface Poll {
  id: string;
  question: string;
  options: PollOption[];
  createdBy: string;
  createdAt: number;
  state: 'open' | 'active' | 'closed';
  stayActive: boolean;
}

interface PollResults {
  pollId: string;
  totalVotes: number;
  results: {
    optionId: string;
    emoji: string;
    label?: string;
    votes: number;
    percentage: number;
    voters: string[];
  }[];
}

interface Player {
  id: string;
  nickname: string;
  socketId: string;
  connected: boolean;
  isHost: boolean;
}

interface PollContextType {
  socket: Socket | null;
  connected: boolean;
  currentPoll: Poll | null;
  pollResults: PollResults | null;
  players: Player[];
  pollEnded: boolean;
  joinPollRoom: (roomId: string, nickname: string, playerId: string) => void;
  submitVote: (roomId: string, playerId: string, optionId: string) => void;
  resetPollState: () => void;
  disconnect: () => void;
}

const PollContext = createContext<PollContextType | undefined>(undefined);

export function usePoll() {
  const context = useContext(PollContext);
  if (context === undefined) {
    throw new Error('usePoll must be used within a PollProvider');
  }
  return context;
}

interface PollProviderProps {
  children: ReactNode;
}

export function PollProvider({ children }: PollProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [currentPoll, setCurrentPoll] = useState<Poll | null>(null);
  const [pollResults, setPollResults] = useState<PollResults | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [pollEnded, setPollEnded] = useState(false);

  useEffect(() => {
    // Initialize socket connection
    const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
    console.log('🔌 Connecting to poll server:', serverUrl);
    
    const socketInstance = io(`${serverUrl}/poll`, {
      path: '/ws/',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: 5,
      timeout: 20000,
      forceNew: false, // Reuse existing connection
    });

    setSocket(socketInstance);

    // Connection event handlers
    socketInstance.on('connect', () => {
      console.log('🔌 Connected to poll server');
      console.log('🔌 Poll socket ID:', socketInstance.id);
      console.log('🔌 Poll socket connected:', socketInstance.connected);
      setConnected(true);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('🔌 Disconnected from poll server:', reason);
      console.log('🔌 Disconnect reason details:', reason);
      setConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('🔌 Poll connection error:', error);
      setConnected(false);
    });

    // Poll event handlers
    socketInstance.on('players_updated', (data: { players: Player[] }) => {
      console.log('👥 Poll players updated:', data.players);
      setPlayers(data.players);
    });

    socketInstance.on('joined_poll_room', (data: { 
      roomId: string; 
      playerId: string;
    }) => {
      console.log('✅ Successfully joined poll room:', data);
    });

    socketInstance.on('player_left', (data: { playerId: string }) => {
      console.log('🚪 Player left poll room:', data.playerId);
    });

    socketInstance.on('poll_started', (data: { poll: Poll }) => {
      console.log('📊 Poll started event received:', {
        pollId: data.poll?.id,
        question: data.poll?.question,
        state: data.poll?.state,
        isActive: (data.poll as any)?.isActive,
        optionsCount: data.poll?.options?.length
      });
      setCurrentPoll(data.poll);
      setPollResults(null);
      setPollEnded(false);
    });

    socketInstance.on('poll_results_updated', (results: PollResults) => {
      console.log('📊 Poll results updated:', results);
      setPollResults(prevResults => {
        // Only update if results actually changed (optimized comparison)
        if (!prevResults || 
            prevResults.totalVotes !== results.totalVotes ||
            prevResults.pollId !== results.pollId ||
            prevResults.results.length !== results.results.length) {
          return results;
        }
        
        // Quick comparison of vote counts
        for (let i = 0; i < results.results.length; i++) {
          if (prevResults.results[i].votes !== results.results[i].votes) {
            return results;
          }
        }
        
        return prevResults; // No changes detected
      });
    });

    socketInstance.on('poll_ended', (data: { results: PollResults }) => {
      console.log('📊 Poll ended with results:', data.results);
      setPollResults(data.results);
      setPollEnded(true);
    });

    socketInstance.on('error', (error: { message: string }) => {
      console.error('❌ Poll socket error:', error.message);
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 Cleaning up poll socket connection');
      socketInstance.disconnect();
    };
  }, []);

  const joinPollRoom = useCallback((roomId: string, nickname: string, playerId: string) => {
    console.log('🚪 joinPollRoom called:', { roomId, nickname, playerId, socket: !!socket, connected });
    if (socket && connected) {
      console.log('🚪 Emitting join_poll_room event:', { roomId, nickname, playerId });
      socket.emit('join_poll_room', { roomId, nickname, playerId });
    } else {
      console.warn('⚠️ Cannot join poll room - socket not connected', { socket: !!socket, connected });
    }
  }, [socket, connected]);

  const submitVote = useCallback((roomId: string, playerId: string, optionId: string) => {
    if (socket && connected) {
      console.log('🗳️ Submitting vote:', { roomId, playerId, optionId });
      socket.emit('submit_vote', { roomId, playerId, optionId });
    } else {
      console.warn('⚠️ Cannot submit vote - socket not connected');
    }
  }, [socket, connected]);

  const resetPollState = useCallback(() => {
    setCurrentPoll(null);
    setPollResults(null);
    setPollEnded(false);
  }, []);

  const disconnect = useCallback(() => {
    if (socket) {
      console.log('🔌 Manually disconnecting poll socket');
      socket.disconnect();
    }
  }, [socket]);

  const value: PollContextType = {
    socket,
    connected,
    currentPoll,
    pollResults,
    players,
    pollEnded,
    joinPollRoom,
    submitVote,
    resetPollState,
    disconnect,
  };

  return (
    <PollContext.Provider value={value}>
      {children}
    </PollContext.Provider>
  );
}
